import React, { useContext, useRef, useEffect } from 'react';
import { SidebarContext } from 'context/SidebarContext';
import { Transition } from '@headlessui/react';
import { ThemeContext } from 'context/ThemeContext';
import SSOService from 'utils/network/sso/sso.service';
import { STATUS_SUCCESS, MESSAGE_ERROR_INTERNAL_SERVER } from 'utils/constants';
import { useNotif } from 'context/NotificationContext';

export default function Header() {
  const profileRef = useRef();
  const { notification, setNotification } = useNotif();

  const {
    // isSideMenuOpen,
    isProfileMenuOpen,
    toggleSideMenu,
    toggleProfileMenu,
    closeProfileMenu
  } = useContext(SidebarContext);

  const { toggleTheme } = useContext(ThemeContext);

  async function handleLogout() {
    try {
      const tokenSso = localStorage.getItem('tokenSso');
      const sessionMd5Email = localStorage.getItem('md_email');

      if (sessionMd5Email) {
        const responeLogout = await SSOService.logoutSso(tokenSso);
        if (responeLogout.status !== STATUS_SUCCESS) {
          setNotification({
            ...notification,
            show: true,
            title: responeLogout.message,
            error: true
          });
          return;
        }
      }

      localStorage.clear();
      window.location.href = '/login';
    } catch (error) {
      setNotification({
        ...notification,
        show: true,
        title: MESSAGE_ERROR_INTERNAL_SERVER,
        error: true
      });
    }
  }

  function handleClickOutside(e) {
    if (
      profileRef &&
      profileRef.current &&
      !profileRef.current.contains(e.target)
    ) {
      closeProfileMenu();
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <header className="z-10 py-4 shadow-md bg-main dark:bg-gray-800">
      <div className="container flex items-center justify-between h-full px-6 mx-auto text-purple-600 dark:text-purple-300">
        {/* <!-- Mobile hamburger --> */}
        <button
          className="p-1 mr-5 -ml-1 rounded-md md:hidden focus:outline-none focus:shadow-outline-purple"
          // @click="toggleSideMenu"
          onClick={toggleSideMenu}
          aria-label="Menu"
        >
          <svg
            className="w-6 h-6"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            ></path>
          </svg>
        </button>
        {/* <!-- Search input --> */}
        <div className="flex justify-center flex-1 lg:mr-32">
          <div className="relative w-full max-w-xl mr-6 focus-within:text-purple-500"></div>
        </div>
        <ul className="flex items-center flex-shrink-0 space-x-6">
          {/* <!-- Theme toggler --> */}
          <li className="flex">
            <button
              className="rounded-md focus:outline-none focus:shadow-outline-purple"
              onClick={toggleTheme}
              aria-label="Toggle color mode"
            >
              <svg
                className="w-5 h-5 hidden dark:block"
                aria-hidden="true"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path>
              </svg>
              <svg
                className="w-5 h-5 block dark:hidden"
                aria-hidden="true"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </button>
          </li>

          {/* <!-- Profile menu --> */}
          <li className="relative" ref={profileRef}>
            <button
              className="align-middle rounded-full focus:shadow-outline-purple focus:outline-none"
              // @click="toggleProfileMenu"
              // @keydown.escape="closeProfileMenu"
              onClick={toggleProfileMenu}
              onKeyDown={toggleProfileMenu}
              aria-label="Account"
              aria-haspopup="true"
            >
              <img
                className="object-cover w-8 h-8 rounded-full"
                src="https://images.unsplash.com/photo-1502378735452-bc7d86632805?ixlib=rb-0.3.5&q=80&fm=jpg&crop=entropy&cs=tinysrgb&w=200&fit=max&s=aa3a807e1bbdfd4364d1f449eaa96d82"
                alt=""
                aria-hidden="true"
              />
            </button>
            <Transition
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              enter="transition ease-in duration-150"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              show={isProfileMenuOpen}
            >
              <ul
                onClick={toggleProfileMenu}
                className="absolute right-0 w-56 p-2 mt-2 text-gray-600 bg-white border border-gray-100 shadow-md space-y-2 rounded-md dark:border-gray-700 dark:text-gray-300 dark:bg-gray-700"
                aria-label="submenu"
              >
                <li className="flex">
                  <button
                    className="inline-flex items-center w-full px-2 py-1 text-sm font-semibold transition-colors duration-150 rounded-md hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    onClick={handleLogout}
                  >
                    <svg
                      className="w-4 h-4 mr-3"
                      aria-hidden="true"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
                    </svg>
                    <span>Log out</span>
                  </button>
                </li>
              </ul>
            </Transition>
          </li>
        </ul>
      </div>
    </header>
  );
}
